import dayjs from "dayjs";
import { useDispatch, useStore } from "react-redux";
import useEventListener from "@use-it/event-listener";

import { copyEventAPI, updateEventAPI } from "../../api";
import { parseEventDate } from "../../api/eventAPI";
import { addEvent, bufferType, clearBuffer, copyEvent, cutEvent, getEventById, updateEvent, updateEventsPositions } from "../../store/events";
import { NO_ROOM_MESSAGE, eventsOnDay, hasRoomFor, isDayFullError, noRoomMessage, rearranged } from "../../functions/dayLimit";
import { setGridDragDay, setGridDragMode } from "../../store/calendar";

// What the pointer is over, for the shortcuts below. Kept outside React state so hovering doesn't re-render.
const hovered = { eventId: null, day: null };

export const hoverEvent = (id) => { hovered.eventId = id; };
export const leaveEvent = () => { hovered.eventId = null; };
export const hoverDay = (day) => { hovered.day = day; };
export const leaveDay = () => { hovered.day = null; };

const COPY_KEYS = ['c', 'C', 'с', 'С'];
const CUT_KEYS = ['x', 'X', 'ч', 'Ч'];
const PASTE_KEYS = ['v', 'V', 'м', 'М'];

const isTyping = (el) => el != null && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

const pasteBufferedEvent = (date) => (dispatch, getState) => {
    const { buffer } = getState().events;
    const event = getEventById(getState(), buffer.id);
    if (event == null) return;
    // Moving an event within its own day doesn't add one there.
    const staysOnItsDay = buffer.type === bufferType.CUT && dayjs(event.date).isSame(date, 'day');
    const target = eventsOnDay(getState(), dayjs(date).format('YYYY-MM-DD'));
    // A copy is a new event, even on the day of the one it copies.
    const incoming = buffer.type === bufferType.COPY ? { width: event.width, height: event.height } : event;
    if (!staysOnItsDay && !hasRoomFor(target, incoming)) {
        alert(noRoomMessage(target));
        return;
    }
    const refused = (e) => {
        if (isDayFullError(e)) alert(NO_ROOM_MESSAGE);
        else console.log(e);
    };
    const placeRearranged = (res) => {
        if (rearranged(res).length) dispatch(updateEventsPositions(rearranged(res)));
    };

    if (buffer.type === bufferType.COPY) {
        copyEventAPI(event, date).then((res) => {
            dispatch(addEvent(parseEventDate(res.data)));
            placeRearranged(res);
        }).catch(refused)
    } else if (buffer.type === bufferType.CUT) {
        updateEventAPI({ ...event, date }).then((res) => {
            dispatch(updateEvent(parseEventDate(res.data)));
            placeRearranged(res);
            dispatch(clearBuffer())
        }).catch(refused)
    }
}

// C / X copy or cut the event under the pointer, V pastes it into the day under the pointer, Esc drops it,
// and holding Ctrl / Cmd lets events be rearranged within their day. Keys are handled when pressed rather
// than kept in component state, so pressing one doesn't re-render the calendar.
export const useCalendarShortcuts = () => {
    const dispatch = useDispatch();
    const store = useStore();

    const setDragMode = (on) => {
        if (store.getState().calendar.gridDragMode !== on) {
            dispatch(setGridDragMode(on));
            if (on && hovered.day != null) dispatch(setGridDragDay(dayjs(hovered.day).format('YYYY-MM-DD')));
        }
    };

    // The listener target is always passed: the hook's default is Node's `global`, which browsers don't have.
    useEventListener('keydown', (e) => {
        if (e.ctrlKey || e.metaKey) setDragMode(true);
        if (e.key === 'Escape') {
            dispatch(clearBuffer());
            return;
        }
        // Letters typed into a field are text, not shortcuts.
        if (e.repeat || isTyping(e.target)) return;

        const state = store.getState();
        if (COPY_KEYS.includes(e.key) && getEventById(state, hovered.eventId)) {
            dispatch(copyEvent(hovered.eventId));
        } else if (CUT_KEYS.includes(e.key) && getEventById(state, hovered.eventId)) {
            dispatch(cutEvent(hovered.eventId));
        } else if (PASTE_KEYS.includes(e.key) && hovered.day != null) {
            dispatch(pasteBufferedEvent(hovered.day));
        }
    }, window);
    useEventListener('keyup', (e) => {
        if (e.key === 'Control' || e.key === 'Meta') setDragMode(false);
    }, window);
    useEventListener('blur', () => setDragMode(false), window);
}
